/**
 * Operator Portal Hooks
 *
 * TanStack Query hooks for the operator training portal:
 * - Dashboard stats
 * - Training list
 * - Progress tracking
 * - Completion
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// TYPES
// ============================================

export interface OperatorInfo {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  departmentName: string | null;
  shiftName: string | null;
  positionName: string | null;
  zoneName: string | null;
}

export interface DashboardStats {
  totalAssignments: number;
  completedAssignments: number;
  inProgressAssignments: number;
  overdueAssignments: number;
  overallProgress: number;
}

export interface OperatorDashboard {
  operator: OperatorInfo | null;
  stats: DashboardStats;
  hasCompletedOnboarding?: boolean;
  coachEnabled?: boolean;
  message?: string;
}

export type MaterialType = 'video' | 'document' | 'quiz';

// Phase 25.4 — Concentric Training Distribution surface.
// `variant` drives card framing (targeted vs refresher vs focused_drill);
// `tier` drives Personal (1) vs Team (2) grouping;
// `fanoutGroupId` clusters bundled Ring 1 items into a BundleCard
// when two or more items share the same group.
export type AssignmentVariant = 'targeted' | 'refresher' | 'focused_drill';
export type BundleSlot = 'video' | 'quiz' | 'single';

export interface TrainingItem {
  assignmentId: string;
  material: {
    id: string;
    title: string;
    description: string | null;
    materialType: MaterialType;
    duration: number | null;
  };
  sopName: string | null;
  sopId: string | null;
  sectionId: string | null;
  sectionTitle: string | null;
  dueDate: string | null;
  status: 'assigned' | 'in_progress' | 'completed';
  progress: number;
  completedAt: string | null;
  createdAt: string;
  isOverdue: boolean;
  relevanceScore: number;
  gapDescription: string | null;
  currentScoreLabel: string | null;
  targetScoreLabel: string | null;
  generatedBy: string;
  isRecommended: boolean;
  // Phase 25.4 — Concentric Training Distribution.
  variant: AssignmentVariant;
  tier: number;
  fanoutGroupId: string | null;
  bundleSlot: BundleSlot;
}

export interface TrainingDetail {
  assignmentId: string;
  material: {
    id: string;
    title: string;
    description: string | null;
    materialType: MaterialType;
    duration: number | null;
    // Phase 25.4: quiz materials return undefined streamUrl — the frontend
    // should route to `/operator/practice/quiz/:sectionId` via sectionId
    // instead of attempting to render a broken video/document viewer.
    streamUrl?: string;
    streamUrlExpiresIn: number;
    sectionId: string | null;
  };
  dueDate: string | null;
  status: 'assigned' | 'in_progress' | 'completed';
  progress: number;
  completedAt: string | null;
  isOverdue: boolean;
  gapDescription: string | null;
  currentScoreLabel: string | null;
  targetScoreLabel: string | null;
  sopName: string | null;
  sopId: string | null;
  generatedBy: string;
}

// ============================================
// SOP REFERENCE TYPES (Phase 21.5.3)
// ============================================

export interface OperatorSop {
  id: string;
  title: string;
  version: string;
  jobCount: number;
  lastAssessedDate: string | null;
}

export interface SopSection {
  id: string;
  sectionNumber: number;
  title: string;
  description: string | null;
  tasks: SopTask[];
}

export interface SopTask {
  id: string;
  sectionId: string | null;
  taskNumber: number;
  title: string;
  description: string | null;
  estimatedDuration: number | null;
  isSafetyCritical: boolean;
  steps: SopStep[];
}

export interface SopStep {
  id: string;
  taskId: string | null;
  stepNumber: number;
  description: string;
  isSafetyCritical: boolean;
}

export interface OperatorSopDetail {
  id: string;
  title: string;
  version: string;
  sections: SopSection[];
}

// ============================================
// BRIEFING TYPES (Phase 21.2.5)
// ============================================

export interface BriefingSop {
  id: string;
  title: string;
  latestScore: number | null;
  categoryScores: {
    compliance: number;
    efficiency: number;
    safety: number;
  } | null;
}

export interface BriefingAssessment {
  id: string;
  date: string;
  zoneName: string | null;
  sopTitle: string;
  taskFocus: string | null;
  aiSummary: string | null;
  trend: 'improving' | 'steady' | 'declining' | 'first';
}

export interface BriefingSuggestedContent {
  assignmentId: string;
  title: string;
  materialType: 'video' | 'document';
  duration: number | null;
}

export interface OperatorBriefing {
  focusSops: BriefingSop[];
  recentAssessments: BriefingAssessment[];
  suggestedContent: BriefingSuggestedContent[];
}

// ============================================
// BRIEFING TYPES (Phase 21.2.5)
// ============================================

export interface BriefingSop {
  id: string;
  title: string;
  latestScore: number | null;
  categoryScores: {
    compliance: number;
    efficiency: number;
    safety: number;
  } | null;
}

export interface BriefingAssessment {
  id: string;
  date: string;
  zoneName: string | null;
  sopTitle: string;
  taskFocus: string | null;
  aiSummary: string | null;
  trend: 'improving' | 'steady' | 'declining' | 'first';
}

export interface BriefingSuggestedContent {
  assignmentId: string;
  title: string;
  materialType: 'video' | 'document';
  duration: number | null;
}

export interface OperatorBriefing {
  focusSops: BriefingSop[];
  recentAssessments: BriefingAssessment[];
  suggestedContent: BriefingSuggestedContent[];
}

// ============================================
// QUERY KEYS
// ============================================

export const operatorPortalKeys = {
  all: ['operator-portal'] as const,
  dashboard: () => [...operatorPortalKeys.all, 'dashboard'] as const,
  briefing: () => [...operatorPortalKeys.all, 'briefing'] as const,
  training: () => [...operatorPortalKeys.all, 'training'] as const,
  trainingList: () => [...operatorPortalKeys.training(), 'list'] as const,
  trainingDetail: (id: string) => [...operatorPortalKeys.training(), 'detail', id] as const,
  sops: () => [...operatorPortalKeys.all, 'sops'] as const,
  sopDetail: (id: string) => [...operatorPortalKeys.all, 'sops', id] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Fetch operator dashboard stats
 */
export function useOperatorDashboard() {
  const api = useApi();

  return useQuery({
    queryKey: operatorPortalKeys.dashboard(),
    queryFn: () => api.get<OperatorDashboard>('/api/operator/dashboard'),
  });
}

/**
 * Fetch pre-shift briefing data (Phase 21.2.5)
 * Focus SOPs, recent assessment highlights (no raw scores), suggested content.
 */
export function useOperatorBriefing() {
  const api = useApi();

  return useQuery({
    queryKey: operatorPortalKeys.briefing(),
    queryFn: () => api.get<OperatorBriefing>('/api/operator/briefing'),
    staleTime: 5 * 60 * 1000, // 5 min -- briefing data doesn't change frequently
  });
}

/**
 * Fetch operator's training list
 */
export function useOperatorTraining() {
  const api = useApi();

  return useQuery({
    queryKey: operatorPortalKeys.trainingList(),
    queryFn: () => api.get<TrainingItem[]>('/api/operator/training'),
  });
}

/**
 * Fetch specific training assignment with stream URL
 */
export function useTrainingDetail(assignmentId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: operatorPortalKeys.trainingDetail(assignmentId ?? ''),
    queryFn: () => api.get<TrainingDetail>(`/api/operator/training/${assignmentId}`),
    enabled: !!assignmentId,
    // Refetch when returning to tab (URL might have expired)
    refetchOnWindowFocus: true,
  });
}

/**
 * Fetch SOPs the operator is assessed against (Phase 21.5.3)
 * Zone-based filtering with assessment-history fallback.
 */
export function useOperatorSops() {
  const api = useApi();
  return useQuery({
    queryKey: operatorPortalKeys.sops(),
    queryFn: () => api.get<OperatorSop[]>('/api/operator/sops'),
    staleTime: 10 * 60 * 1000, // 10 min — SOPs don't change frequently
  });
}

/**
 * Fetch read-only SOP hierarchy detail (Phase 21.5.3)
 * Returns sections (Jobs) → tasks (Tasks) → steps (Steps).
 */
export function useOperatorSopDetail(sopId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: operatorPortalKeys.sopDetail(sopId ?? ''),
    queryFn: () => api.get<OperatorSopDetail>(`/api/operator/sops/${sopId}`),
    enabled: !!sopId,
    staleTime: 30 * 60 * 1000, // 30 min — SOP content is very stable
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Update training progress
 */
export function useUpdateProgress() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assignmentId, progress }: { assignmentId: string; progress: number }) =>
      api.put<{ assignmentId: string; progress: number; status: string }>(
        `/api/operator/training/${assignmentId}/progress`,
        { progress }
      ),
    onSuccess: (_, variables) => {
      // Update the training detail cache optimistically
      queryClient.setQueryData(
        operatorPortalKeys.trainingDetail(variables.assignmentId),
        (old: TrainingDetail | undefined) =>
          old
            ? {
                ...old,
                progress: Math.max(old.progress, variables.progress),
                status: 'in_progress' as const,
              }
            : old
      );
      // Invalidate list to reflect changes
      queryClient.invalidateQueries({ queryKey: operatorPortalKeys.trainingList() });
      queryClient.invalidateQueries({ queryKey: operatorPortalKeys.dashboard() });
    },
  });
}

/**
 * Mark training as complete
 */
export function useCompleteTraining() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.post<{ assignmentId: string; status: string; progress: number; completedAt: string }>(
        `/api/operator/training/${assignmentId}/complete`
      ),
    onSuccess: () => {
      // Invalidate all portal queries to refresh data
      queryClient.invalidateQueries({ queryKey: operatorPortalKeys.all });
    },
  });
}
